import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/config';
import DashboardHeader from '../common/DashboardHeader';
import {
  WrenchScrewdriverIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  CubeIcon,
  ChevronRightIcon,
  PlayIcon,
  PauseIcon,
  DocumentTextIcon,
  CalendarIcon,
  BellIcon,
  TruckIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface Task {
  id: string;
  jobCardId: string;
  title: string;
  description: string;
  vehicle: string;
  customer: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed' | 'paused';
  estimatedTime: number; // in minutes
  timeSpent: number; // in minutes
  assignedAt: string;
  dueTime: string;
  parts?: string[];
}

interface DashboardStats {
  assignedTasks: number;
  completedToday: number;
  inProgress: number;
  pendingTasks: number;
  averageCompletionTime: number;
  performanceScore: number;
  todayEarnings: number;
  monthlyEarnings: number;
}

interface Activity {
  id: string;
  type: 'task_started' | 'task_completed' | 'part_requested' | 'break_taken' | 'customer_feedback';
  title: string;
  description: string;
  timestamp: string;
  icon?: React.ReactNode;
}

interface InventoryRequest {
  id: string;
  partName: string;
  quantity: number;
  status: 'pending' | 'approved' | 'delivered';
  requestedAt: string;
  jobCardId: string;
}

const EmployeeDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [inventoryRequests, setInventoryRequests] = useState<InventoryRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [timer, setTimer] = useState<number>(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerRunning && activeTaskId) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, activeTaskId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch dashboard stats
      const [statsRes, tasksRes, notificationsRes] = await Promise.all([
        apiClient.get('/employee/dashboard-stats').catch(err => {
          console.error('Failed to fetch employee stats:', err);
          return { data: { data: {} } };
        }),
        apiClient.get('/employee/tasks').catch(err => {
          console.error('Failed to fetch employee tasks:', err);
          return { data: { data: [] } };
        }),
        apiClient.get('/employee/notifications').catch(err => {
          console.error('Failed to fetch employee notifications:', err);
          return { data: { data: [] } };
        })
      ]);

      // Set stats
      setStats(statsRes.data.data || {
        assignedTasks: 0,
        completedToday: 0,
        inProgress: 0,
        pendingTasks: 0,
        averageCompletionTime: 0,
        performanceScore: 0,
        todayEarnings: 0,
        monthlyEarnings: 0,
      });

      // Process tasks
      const tasksData = tasksRes.data.data || [];
      if (tasksData.length > 0) {
        setTasks(tasksData.map((task: any) => ({
          id: task.id.toString(),
          jobCardId: task.jobCardNumber,
          title: task.title || task.description,
          description: task.description,
          vehicle: `${task.vehicleMake} ${task.vehicleModel} ${task.vehicleYear} - ${task.vehicleColor}`,
          customer: task.customerName,
          priority: task.priority || 'medium',
          status: task.status,
          estimatedTime: task.estimatedTime || 60,
          timeSpent: task.timeSpent || 0,
          assignedAt: new Date(task.assignedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          dueTime: task.dueTime ? new Date(task.dueTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '',
          parts: task.parts || [],
        })));
      } else {
        // Set some mock tasks if no data available
        setTasks([
          {
            id: '1',
            jobCardId: 'JOB-202412-0001',
            title: 'Oil Change Service',
            description: 'Regular oil change and filter replacement',
            vehicle: 'Toyota Camry 2020 - Silver',
            customer: 'Ahmed Al-Rashid',
            priority: 'medium' as const,
            status: 'pending' as const,
            estimatedTime: 45,
            timeSpent: 0,
            assignedAt: '09:00',
            dueTime: '11:00',
            parts: ['Oil Filter', 'Engine Oil 5L'],
          },
          {
            id: '2',
            jobCardId: 'JOB-202412-0002',
            title: 'Brake Inspection',
            description: 'Comprehensive brake system inspection',
            vehicle: 'Honda Civic 2019 - Blue',
            customer: 'Fatima Al-Zahra',
            priority: 'high' as const,
            status: 'in_progress' as const,
            estimatedTime: 90,
            timeSpent: 30,
            assignedAt: '10:30',
            dueTime: '14:00',
            parts: ['Brake Pads'],
          }
        ]);
      }

      // Process activities from notifications
      const notificationsData = notificationsRes.data.data || [];
      setActivities(notificationsData.slice(0, 10).map((notif: any) => ({
        id: notif.id.toString(),
        type: notif.type || 'task_started',
        title: notif.title,
        description: notif.message,
        timestamp: getRelativeTime(notif.createdAt),
      })));

      // Set inventory requests (may need a separate endpoint)
      setInventoryRequests([]);

    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Set default empty data on error
      setStats({
        assignedTasks: 0,
        completedToday: 0,
        inProgress: 0,
        pendingTasks: 0,
        averageCompletionTime: 0,
        performanceScore: 0,
        todayEarnings: 0,
        monthlyEarnings: 0,
      });
      setTasks([]);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const startTask = (taskId: string) => {
    setActiveTaskId(taskId);
    setIsTimerRunning(true);
    setTimer(0);
    
    // Update task status
    setTasks(prev => prev.map(task => 
      task.id === taskId ? { ...task, status: 'in_progress' as const } : task
    ));
  };

  const pauseTask = () => {
    setIsTimerRunning(false);
  };

  const completeTask = (taskId: string) => {
    setActiveTaskId(null);
    setIsTimerRunning(false);
    setTimer(0);
    
    // Update task status
    setTasks(prev => prev.map(task => 
      task.id === taskId ? { ...task, status: 'completed' as const, timeSpent: Math.floor(timer / 60) } : task
    ));
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600 bg-red-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'in_progress':
        return 'text-blue-600 bg-blue-100';
      case 'pending':
        return 'text-gray-600 bg-gray-100';
      case 'paused':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const renderPerformanceChart = () => {
    const data = {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      datasets: [
        {
          label: 'Tasks Completed',
          data: [4, 6, 5, 7, 5, 8],
          borderColor: '#008B8B',
          backgroundColor: 'rgba(0, 139, 139, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: 'This Week Performance',
        },
      },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    };

    return <Line data={data} options={options} />;
  };

  const renderTaskDistribution = () => {
    const data = {
      labels: ['Completed', 'In Progress', 'Pending'],
      datasets: [
        {
          data: [5, 2, 5],
          backgroundColor: ['#10B981', '#3B82F6', '#9CA3AF'],
          borderWidth: 2,
          borderColor: '#ffffff',
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom' as const,
        },
        title: {
          display: true,
          text: 'Today\'s Task Status',
        },
      },
    };

    return <Doughnut data={data} options={options} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader 
        userName={user?.name}
        userRole="employee"
        showNotifications={true}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Welcome back, {user?.name || 'Technician'}
              </h1>
              <p className="text-gray-600 mt-1">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-4">
              {isTimerRunning && (
                <div className="bg-blue-100 px-4 py-2 rounded-lg flex items-center">
                  <ClockIcon className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-blue-600 font-medium">{formatTime(timer)}</span>
                </div>
              )}
              <button className="bg-[#008B8B] text-white px-4 py-2 rounded-lg hover:bg-[#006666]">
                Take Break
              </button>
            </div>
          </div>
        </div>

        {/* Performance Alert */}
        {stats && stats.performanceScore >= 90 && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <StarIcon className="w-5 h-5 text-green-600 mr-2" />
              <p className="text-sm text-green-800">
                Great job! Your performance score is <span className="font-semibold">{stats.performanceScore}%</span>. 
                Keep up the excellent work!
              </p>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#008B8B]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Assigned Tasks</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.assignedTasks || 0}</p>
                <p className="text-sm text-gray-500 mt-1">For today</p>
              </div>
              <div className="p-4 bg-[#008B8B]/10 rounded-xl">
                <WrenchScrewdriverIcon className="w-8 h-8 text-[#008B8B]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#10B981]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed Today</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.completedToday || 0}</p>
                <div className="mt-2 flex items-center">
                  <ArrowUpIcon className="w-4 h-4 text-green-500 mr-1" />
                  <span className="text-sm text-green-600">+2 vs yesterday</span>
                </div>
              </div>
              <div className="p-4 bg-[#10B981]/10 rounded-xl">
                <CheckCircleIcon className="w-8 h-8 text-[#10B981]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#F59E0B]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.inProgress || 0}</p>
                <p className="text-sm text-gray-500 mt-1">Active now</p>
              </div>
              <div className="p-4 bg-[#F59E0B]/10 rounded-xl">
                <ClockIcon className="w-8 h-8 text-[#F59E0B]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-[#8B5CF6]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Performance</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.performanceScore || 0}%</p>
                <p className="text-sm text-gray-500 mt-1">This month</p>
              </div>
              <div className="p-4 bg-[#8B5CF6]/10 rounded-xl">
                <StarIcon className="w-8 h-8 text-[#8B5CF6]" />
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Avg Completion Time</p>
                <p className="text-xl font-bold text-gray-900">{stats?.averageCompletionTime || 0} min</p>
                <p className="text-xs text-green-500 mt-1">-5 min vs avg</p>
              </div>
              <ClockIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Today's Earnings</p>
                <p className="text-xl font-bold text-gray-900">{stats?.todayEarnings || 0} KWD</p>
                <p className="text-xs text-gray-500 mt-1">Commission based</p>
              </div>
              <CurrencyDollarIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Parts Requested</p>
                <p className="text-xl font-bold text-gray-900">{inventoryRequests.length}</p>
                <p className="text-xs text-orange-500 mt-1">1 pending</p>
              </div>
              <CubeIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600">Customer Rating</p>
                <p className="text-xl font-bold text-gray-900">⭐ 4.8</p>
                <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
              </div>
              <StarIcon className="w-6 h-6 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Active Tasks */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <WrenchScrewdriverIcon className="w-5 h-5 text-[#008B8B] mr-2" />
            My Active Tasks
          </h2>
          <div className="space-y-4">
            {tasks.filter(task => task.status !== 'completed').map((task) => (
              <div key={task.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-sm font-medium text-gray-500">#{task.jobCardId}</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getPriorityColor(task.priority)}`}>
                        {task.priority.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(task.status)}`}>
                        {task.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                    
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center text-sm text-gray-500">
                        <TruckIcon className="w-4 h-4 mr-2" />
                        <span>{task.vehicle}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <UsersIcon className="w-4 h-4 mr-2" />
                        <span>{task.customer}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <ClockIcon className="w-4 h-4 mr-2" />
                        <span>Est. {task.estimatedTime} min • Due by {task.dueTime}</span>
                      </div>
                    </div>

                    {task.parts && task.parts.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">Required Parts:</p>
                        <div className="flex flex-wrap gap-2">
                          {task.parts.map((part, index) => (
                            <span key={index} className="px-2 py-1 bg-gray-100 text-xs text-gray-700 rounded">
                              {part}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex items-center space-x-3">
                      {task.status === 'pending' && (
                        <button
                          onClick={() => startTask(task.id)}
                          className="px-4 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666] flex items-center"
                        >
                          <PlayIcon className="w-4 h-4 mr-2" />
                          Start Task
                        </button>
                      )}
                      {task.status === 'in_progress' && task.id === activeTaskId && (
                        <>
                          <button
                            onClick={pauseTask}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center"
                          >
                            <PauseIcon className="w-4 h-4 mr-2" />
                            Pause
                          </button>
                          <button
                            onClick={() => completeTask(task.id)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                          >
                            <CheckCircleIcon className="w-4 h-4 mr-2" />
                            Complete
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => navigate(`/dashboard/job-cards/${task.jobCardId}`)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        View Details
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="ml-6 w-32">
                    <p className="text-sm text-gray-600 mb-2">Progress</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-[#008B8B] h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${(task.timeSpent / task.estimatedTime) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {task.timeSpent} / {task.estimatedTime} min
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Performance Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="h-64">
              {renderPerformanceChart()}
            </div>
          </div>

          {/* Task Distribution */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="h-64">
              {renderTaskDistribution()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
            <div className="space-y-4">
              {activities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {activity.type === 'task_completed' && <CheckCircleIcon className="w-5 h-5 text-green-600" />}
                    {activity.type === 'task_started' && <PlayIcon className="w-5 h-5 text-blue-600" />}
                    {activity.type === 'part_requested' && <CubeIcon className="w-5 h-5 text-orange-600" />}
                    {activity.type === 'customer_feedback' && <StarIcon className="w-5 h-5 text-yellow-600" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-600">{activity.description}</p>
                    <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Inventory Requests */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Parts Requests</h2>
              <button 
                onClick={() => navigate('/dashboard/inventory')}
                className="text-sm text-[#008B8B] hover:text-[#006666] font-medium"
              >
                Request Parts
              </button>
            </div>
            <div className="space-y-4">
              {inventoryRequests.map((request) => (
                <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{request.partName}</p>
                      <p className="text-sm text-gray-600">
                        Job #{request.jobCardId} • Qty: {request.quantity}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{request.requestedAt}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      request.status === 'approved' ? 'bg-green-100 text-green-800' :
                      request.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {request.status.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
              {inventoryRequests.length === 0 && (
                <p className="text-center text-gray-500 py-4">No pending parts requests</p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/dashboard/job-cards')}
              className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow text-left"
            >
              <DocumentTextIcon className="w-6 h-6 text-[#008B8B] mb-2" />
              <h3 className="font-medium text-gray-900">View All Jobs</h3>
              <p className="text-sm text-gray-600">See assigned job cards</p>
            </button>

            <button 
              onClick={() => navigate('/dashboard/inventory')}
              className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow text-left"
            >
              <CubeIcon className="w-6 h-6 text-[#FF8C00] mb-2" />
              <h3 className="font-medium text-gray-900">Request Parts</h3>
              <p className="text-sm text-gray-600">Order inventory items</p>
            </button>

            <button 
              onClick={() => navigate('/dashboard/customers')}
              className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow text-left"
            >
              <ChatBubbleLeftRightIcon className="w-6 h-6 text-[#10B981] mb-2" />
              <h3 className="font-medium text-gray-900">Customer Info</h3>
              <p className="text-sm text-gray-600">View customer details</p>
            </button>

            <button 
              onClick={() => navigate('/dashboard/schedule')}
              className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow text-left"
            >
              <CalendarIcon className="w-6 h-6 text-[#8B5CF6] mb-2" />
              <h3 className="font-medium text-gray-900">My Schedule</h3>
              <p className="text-sm text-gray-600">View work calendar</p>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmployeeDashboard;